import { useHistoricYear } from "@/contexts/HistoricYearContext";
import { LazoCompany, SimpleUser } from "@/models/LazoCompany";
import LazoCompanyService from "@/services/LazoCompanyService";
import { RequiredProfileInformationDTO } from "@/types/RequiredProfileInformationDTO";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

export const LAZO_COMPANY_QUERY_KEY = "lazo-company";

export function useWorkspaceBaseUrl(tenantExternalId: string | undefined) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "workspace-base-url", tenantExternalId],
    queryFn: () => LazoCompanyService.getWorkspaceBaseUrl(tenantExternalId!).then((r) => r.getContentOrThrowError()),
    enabled: !!tenantExternalId,
  });
}

// ========== QUERIES ==========

export function useLazoCompaniesByOwnerExternalId(externalId: string) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "by-owner", externalId],
    queryFn: () => LazoCompanyService.getAllCompaniesByOwnerExternalId(externalId).then((r) => r.getContentOrThrowError()),
    enabled: !!externalId,
    initialData: [],
    meta: {
      errorMessage: "Error obtaining user companies",
    },
  });
}

export function useLazoCompanyHistory(companyExternalId: string) {
  const { year } = useHistoricYear();

  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, companyExternalId, year],
    queryFn: () => LazoCompanyService.getCompanyHistory(companyExternalId, year).then((r) => r.getContentOrThrowError()),
    enabled: !!companyExternalId && !!year,
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: "Error obtaining company history",
    },
  });
}

export function useOnboardingCompanies(filters: any, page: number) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "onboarding", filters, page],
    queryFn: () => LazoCompanyService.getOnboardingCompanies(filters, page).then((r) => r.getContentOrThrowError()),
    placeholderData: null,
  });
}

export function useRegistrationLink(companyExternalId: string | null) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "registration-link", companyExternalId],
    queryFn: () => LazoCompanyService.getRegistrationLink(companyExternalId!).then((r) => r.getContentOrThrowError()),
    enabled: !!companyExternalId,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error fetching registration link",
    },
  });
}

export function useRequiredCompanyInformation(companyExternalId: string, isPortal: boolean) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, companyExternalId, "required-profile-information"],
    queryFn: () => LazoCompanyService.getRequiredCompanyInformation(companyExternalId).then((r) => r.getContentOrThrowError()),
    enabled: isPortal,
  });
}

export function useLazoCompanyAutocomplete(searchTerm: string, minLength: number = 2) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "by-term", searchTerm],
    queryFn: () => LazoCompanyService.getByTerm(searchTerm).then((r) => r.getContentOrThrowError()),
    enabled: searchTerm.length >= minLength,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    meta: {
      errorMessage: "Error searching companies",
    },
  });
}

export function useLazoCompanyByTenantExternalId(tenantExternalId: string) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "by-tenant", tenantExternalId],
    queryFn: () => LazoCompanyService.getByTenantExternalId(tenantExternalId).then((r) => r.getContentOrThrowError()),
    enabled: !!tenantExternalId,
    meta: {
      errorMessage: "Error fetching company information",
    },
  });
}

export function useCompaniesForFilter() {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "for-filter"],
    queryFn: () => LazoCompanyService.getCompaniesForFilter().then((r) => r.getContentOrThrowError()),
    meta: {
      errorMessage: "Error fetching companies",
    },
  });
}

export function useCompaniesByOrderNumber(ignitionOrderId: string) {
  return useQuery({
    queryKey: [LAZO_COMPANY_QUERY_KEY, "by-order-number", ignitionOrderId],
    queryFn: () => LazoCompanyService.getCompaniesByOrderNumber(ignitionOrderId).then((r) => r.getContentOrThrowError()),
    enabled: !!ignitionOrderId,
    initialData: [],
    meta: {
      errorMessage: "Error checking order number",
    },
  });
}

// ========== MUTATIONS ==========

export function useCompleteCompanyInformation(companyExternalId: string) {
  return useMutation({
    mutationFn: (data: RequiredProfileInformationDTO) =>
      LazoCompanyService.completeCompanyInformation(companyExternalId, data).then((r) => r.getContentOrThrowError()),
    mutationKey: [LAZO_COMPANY_QUERY_KEY, companyExternalId, "required-profile-information"],
    meta: {
      successMessage: "Company information completed successfully",
      errorMessage: "Error completing company information",
    },
  });
}

interface AssignKamParams {
  companyExternalId: string;
  userExternalId: string;
}

export function useAssignKam() {
  return useMutation<SimpleUser, unknown, AssignKamParams>({
    mutationFn: ({ companyExternalId, userExternalId }) =>
      LazoCompanyService.assignKam(companyExternalId, userExternalId).then((r) => r.getContentOrThrowError()),
    mutationKey: [LAZO_COMPANY_QUERY_KEY],
    meta: {
      successMessage: "KAM assigned successfully",
      errorMessage: "There was an error assigning the company's KAM",
    },
  });
}

interface UpdateCompanyGeneralInfoParams {
  companyExternalId: string;
  onboardingStatus: string;
}

export function useUpdateOnboardingStatus() {
  return useMutation<void, unknown, UpdateCompanyGeneralInfoParams>({
    mutationFn: ({ companyExternalId, onboardingStatus }) =>
      LazoCompanyService.updateOnboardingStatus(companyExternalId, onboardingStatus).then((r) => r.getContentOrThrowError()),
    mutationKey: [LAZO_COMPANY_QUERY_KEY],
    meta: {
      successMessage: "Onboarding status updated successfully",
      errorMessage: "There was an error updating the onboarding status",
    },
  });
}

export function useUpdateCompanyGeneralInfo(externalId: string) {
  const { year } = useHistoricYear();

  return useMutation({
    mutationFn: (data: LazoCompany) => LazoCompanyService.updateGeneralInfo(externalId, data, year).then((r) => r.getContentOrThrowError()),
    // Better to invalidate all company related queries
    mutationKey: [LAZO_COMPANY_QUERY_KEY],
    meta: {
      successMessage: "Company information updated successfully",
      errorMessage: "There was an error updating the company information",
    },
  });
}
